import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Users, Target, Award, TrendingUp } from "lucide-react";

const Index = () => {
  const services = [
    {
      title: "Service Mobile",
      description: "Nous nous déplaçons directement chez vous, particuliers comme professionnels, partout au Sénégal.",
      icon: Target,
    },
    {
      title: "Tous Véhicules",
      description: "Contrôle technique pour véhicules légers, poids lourds et motos selon les normes en vigueur.",
      icon: TrendingUp,
    },
    {
      title: "Agrément Officiel",
      description: "Société agréée par les autorités compétentes (Direction des Transports Terrestres).",
      icon: Users,
    },
  ];

  const advantages = [
    "Service mobile près de chez vous, stationnement facile",
    "Pas de perte de temps dans les files d'attente",
    "Équipe technique certifiée et équipement de pointe",
    "Respect des normes en vigueur et ponctualité garantie",
    "Prise de rendez-vous rapide et accueil chaleureux",
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge variant="secondary" className="w-fit">
                Service Mobile de Contrôle Technique
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Nous venons chez vous pour garantir votre 
                <span className="text-primary"> sécurité</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                CLERYS est une société sénégalaise spécialisée dans le contrôle technique mobile, 
                agréée par les autorités compétentes. Nous nous déplaçons directement chez vous, 
                partout au Sénégal.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" asChild>
                  <Link to="/contact">
                    Prendre rendez-vous
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/services">Nos services</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/equipe_africaine_2.jpeg"
                alt="Technicien africain effectuant un contrôle technique"
                className="rounded-lg shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Service Mobile Innovant
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une solution moderne qui facilite l'accès au contrôle technique en nous déplaçant 
              directement chez nos clients, conformément aux exigences réglementaires.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Advantages Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Atouts
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez les avantages uniques de notre service mobile de contrôle technique.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Service Mobile",
                description: "Près de chez vous, stationnement facile",
                icon: "🚗"
              },
              {
                title: "Gain de Temps",
                description: "Pas de perte de temps dans les files d'attente",
                icon: "⏰"
              },
              {
                title: "Équipe Certifiée",
                description: "Équipe technique certifiée",
                icon: "👨‍🔧"
              },
              {
                title: "Équipement de Pointe",
                description: "Équipement de dernière génération",
                icon: "🔧"
              },
              {
                title: "Conformité",
                description: "Respect des normes en vigueur",
                icon: "✅"
              },
              {
                title: "Ponctualité",
                description: "Nous venons à l'heure convenue",
                icon: "🎯"
              },
              {
                title: "Rendez-vous Rapide",
                description: "Prise de rendez-vous rapide",
                icon: "📞"
              },
              {
                title: "Accueil Chaleureux",
                description: "Accueil chaleureux et conseils personnalisés",
                icon: "🤝"
              }
            ].map((advantage, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-md transition-shadow">
                <div className="text-4xl mb-4">{advantage.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{advantage.title}</h3>
                <p className="text-sm text-gray-600">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/equipe_professionnelle_2.jpeg"
                alt="Équipe de techniciens africains au travail"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Pourquoi choisir Clerys ?
              </h2>
              <p className="text-lg text-gray-600">
                Notre centre combine équipements modernes et expertise technique pour garantir 
                un contrôle fiable et conforme aux standards de sécurité routière.
              </p>
              <ul className="space-y-4">
                {advantages.map((advantage, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{advantage}</span>
                  </li>
                ))}
              </ul>
              <Button asChild>
                <Link to="/about">
                  En savoir plus
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Besoin d'un contrôle technique ?
            </h2>
            <p className="text-xl text-blue-100">
              Prenez rendez-vous dès aujourd'hui dans notre centre agréé. 
              Service rapide et professionnel garanti.
            </p>
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contact">
                Prendre rendez-vous
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;